/**
 * BusinessProcess Class.
 *
 * @module BusinessProcess
 **/
export default class BusinessProcess {
  /**
   * @constructor
   * @this {BusinessProcess}
   *
   * @param {Object} rawBusinessProcess - Business Process stats information.
   **/
  constructor({
    businessProcessASA,
    businessProcessAbandonedInteractions,
    businessProcessAbandonedPercentage,
    businessProcessAnsweredInteractions,
    businessProcessAnsweredPercentage,
    businessProcessAverageAgentHandlingTime,
    businessProcessAverageAnswerTime,
    businessProcessAverageTreatingTimeScore,
    businessProcessAverageWaitingTime,
    businessProcessBackofficeAgents,
    businessProcessBusyAgents,
    businessProcessConsultOrConferenceAgents,
    businessProcessDNCRequestsInShift,
    businessProcessEndedInteractions,
    businessProcessForOutgoingAgents,
    businessProcessHandledByAgentInteractions,
    businessProcessHandledInBusinessProcessInteractions,
    businessProcessId,
    businessProcessIdleAgents,
    businessProcessInBreakAgents,
    businessProcessInProcessInteractions,
    businessProcessInternalAgents,
    businessProcessLoggedInAgents,
    businessProcessLongestWaitingTime,
    businessProcessName,
    businessProcessNoAnswerAgents,
    businessProcessOverflowInteractions,
    businessProcessPrivateAgents,
    businessProcessRequestCallbackInteractions,
    businessProcessServiceLevel,
    businessProcessTotalExternalTransferredOut,
    businessProcessTotalInteractions,
    businessProcessTotalInternalTransferredOut,
    businessProcessTreatedInteractionsScore,
    businessProcessUnavailableAgents,
    businessProcessVoiceMails,
    businessProcessWaitingDeferrableInteractions,
    businessProcessWaitingInteractions,
    callbackAverageHandlingTime,
    callbackFailedInShift,
    callbackFailedPercentageInShift,
    callbackInteractionsPurgedInShift,
    callbackProcessedInShift,
    callbackRequestedInShift,
    callbackRescheduledInShift,
    callbackSucceededInShift,
    callbackSuccessfulPercentageInShift,
    callbackToBeProcessed,
    lastResetTime
  }) {
    Object.assign(this, ...arguments);
  }

  /**
   * Public getters
   */

  /**
   * Returns the total amount of idle agents.
   *
   * @returns {Number}
   */
  get totalReadyAgents() {
    const readyAgents = [this.businessProcessIdleAgents];
    return readyAgents.reduce(sumReducer, 0);
  }

  /**
   * Returns the total amount of agents in Consult, Conference, or any Busy state.
   *
   * @returns {Number}
   */
  get totalBusyAgents() {
    const busyAgents = [this.businessProcessBusyAgents];
    return busyAgents.reduce(sumReducer, 0);
  }

  /**
   * Returns the total amount of agents in Backoffice state.
   *
   * @returns {Number}
   */
  get totalBackofficeAgents() {
    const busyAgents = [this.businessProcessBackofficeAgents];
    return busyAgents.reduce(sumReducer, 0);
  }

  /**
   * Returns the total amount of agents in Outgoing state.
   *
   * @returns {Number}
   */
  get totalOutgoingAgents() {
    const busyAgents = [this.businessProcessForOutgoingAgents];
    return busyAgents.reduce(sumReducer, 0);
  }

  /**
   * Returns the total amount of agents in any kind of "Break" state and "No Answer" state.
   *
   * @returns {Number}
   */
  get totalBreakAgents() {
    const unavailableAgents = [this.businessProcessInBreakAgents, this.businessProcessNoAnswerAgents];
    return unavailableAgents.reduce(sumReducer, 0);
  }

  /**
   * Returns the total amount of unavailable agents.
   *
   * @returns {Number}
   */
  get totalUnavailableAgents() {
    const unavailableAgents = [this.businessProcessUnavailableAgents];
    return unavailableAgents.reduce(sumReducer, 0);
  }

  /**
   * Public methods
   */

  /**
   * Returns the value of a given key.
   *
   * @returns {number|string}
   */
  getValue(key) {
    return this[key].value;
  }

  /**
   * Returns dimensions values for a given key.
   *
   * @returns {Array<Object>}
   */
  getDimensionsValues(key) {
    return this[key].dimensionsValues;
  }

  /**
   * Compares two given BusinessProcess objects for order.
   * Returns a negative integer, zero, or a positive integer as ContactA object is less than,
   * equal to, or greater than ContactB object.
   *
   * @param {BusinessProcess} businessProcessA
   * @param {BusinessProcess} businessProcessB
   *
   * @returns {Number}
   */
  static compare(businessProcessA, businessProcessB) {
    const comparableFieldsA = [businessProcessA.businessProcessName.toUpperCase()].join('');
    const comparableFieldsB = [businessProcessB.businessProcessName.toUpperCase()].join('');

    if (comparableFieldsA < comparableFieldsB) {
      return -1;
    }
    if (comparableFieldsA > comparableFieldsB) {
      return 1;
    }
    return 0;
  }
}

const sumReducer = (sum, stat = {}) => sum + (stat.value || 0);
